import { useCallback, useRef } from "react";
import { ChatStreamCallbacks } from "src/services/api/chat";
import { useMessageStore } from "src/store/messageStore";

/**
 * Batches stream token updates to one Zustand write per animation frame.
 */
export function useBatchedStreamCallbacks() {
    const updateMessage = useMessageStore((s) => s.updateMessage);
    const appendMessageText = useMessageStore((s) => s.appendMessageText);

    const batchRef = useRef<{
        messageId: string;
        buffer: string;
        rafId: number | null;
    }>({ messageId: "", buffer: "", rafId: null });

    const flush = useCallback(
        (messageId: string) => {
            const batch = batchRef.current;
            if (batch.messageId !== messageId || !batch.buffer) {
                batch.rafId = null;
                return;
            }
            appendMessageText(messageId, batch.buffer);
            batch.buffer = "";
            batch.rafId = null;
        },
        [appendMessageText]
    );

    const flushPending = useCallback(
        (messageId: string) => {
            const batch = batchRef.current;
            if (batch.rafId != null) {
                cancelAnimationFrame(batch.rafId);
                batch.rafId = null;
            }
            flush(messageId);
        },
        [flush]
    );

    const createCallbacks = useCallback(
        (messageId: string): ChatStreamCallbacks => ({
            onStatus: (message: string) => {
                updateMessage(messageId, { streamStatus: message });
            },
            onToken: (content: string) => {
                const batch = batchRef.current;
                if (batch.messageId !== messageId) {
                    if (batch.rafId != null) {
                        cancelAnimationFrame(batch.rafId);
                    }
                    batch.messageId = messageId;
                    batch.buffer = "";
                    batch.rafId = null;
                }
                batch.buffer += content;
                if (batch.rafId == null) {
                    batch.rafId = requestAnimationFrame(() => flush(messageId));
                }
            },
        }),
        [flush, updateMessage]
    );

    return { createCallbacks, flushPending };
}
